// Departamentos (nomina.departamentos). Nómina → Personal.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { Campo, Modal, Switch, fieldCls, nom, ETAPAS_PRODUCTIVAS, useDialogoEnLinea, type Departamento } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

const vacio = { codigo: '', nombre: '', descripcion: '', es_activo: true, etapa_productiva: '' };

export function Departamentos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Departamento[]>({
    queryKey: ['nom-departamentos'],
    queryFn: () => nom.list<Departamento>('departamentos', '?select=*&deleted_at=is.null&order=codigo'),
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (d?: Departamento) => {
    if (d) setForm({ codigo: d.codigo ?? '', nombre: d.nombre, descripcion: d.descripcion ?? '', es_activo: d.es_activo, etapa_productiva: d.etapa_productiva ?? '' });
    else setForm(vacio);
    setEditId(d?.id ?? null); setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const payload = { ...form, etapa_productiva: form.etapa_productiva || null };
      return editId ? nom.update('departamentos', editId, payload) : nom.create('departamentos', payload);
    },
    onSuccess: () => { toast.success('Departamento guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['nom-departamentos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => nom.update('departamentos', id, { deleted_at: new Date().toISOString(), es_activo: false }),
    onSuccess: () => { toast.success('Departamento eliminado'); void qc.invalidateQueries({ queryKey: ['nom-departamentos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const headersExp = ['N°', 'Código', 'Nombre', 'Descripción', 'Etapa productiva', 'Estado'];
  const filasExport: Celda[][] = rows.map((d, i): Celda[] => [
    i + 1, d.codigo, d.nombre, d.descripcion ?? '', d.etapa_productiva ? ETAPAS_PRODUCTIVAS[d.etapa_productiva] ?? d.etapa_productiva : '—', d.es_activo ? 'Activo' : 'Inactivo',
  ]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Departamentos</h1>
        <div className="flex items-center gap-2">
          <ExportarBotones
            nombre={`departamentos-${fechaLocal()}`}
            titulo="Departamentos" headers={headersExp} filas={filasExport} colsDerecha={[0]}
            disabled={rows.length === 0}
          />
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo departamento</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Departamentos ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Descripción</th><th className="py-2 pr-3">Etapa productiva</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin departamentos</td></tr>}
              {rows.map((d) => (
                <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{d.codigo}</td>
                  <td className="py-2 pr-3">{d.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{d.descripcion}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{d.etapa_productiva ? ETAPAS_PRODUCTIVAS[d.etapa_productiva] ?? d.etapa_productiva : '—'}</td>
                  <td className="py-2 pr-3">{d.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(d)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${d.nombre}"?`, { peligro: true, aceptar: 'Eliminar' })) eliminar.mutate(d.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar departamento' : 'Nuevo departamento'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="DEP-11" /></Campo>
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            </div>
            <Campo label="Descripción"><input className={fieldCls} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <Campo label="Etapa productiva">
              <select className={fieldCls} value={form.etapa_productiva} onChange={(e) => setForm({ ...form, etapa_productiva: e.target.value })}>
                <option value="">— (ninguna)</option>
                {Object.entries(ETAPAS_PRODUCTIVAS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
